/**
 * useGiftCardDetailState — fetch state for one gift card (BON-223 detail
 * page). Read-only: there is no note/resolve flow like Needs-review, so this
 * is deliberately thinner than `useReviewDetailState`.
 */
import { useEffect, useState } from 'react';

import { fetchGiftCardDetail } from './giftCardsApi';
import type { GiftCardDetail } from './giftCardTypes';

export function useGiftCardDetailState(id: string) {
  const [detail, setDetail] = useState<GiftCardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);
    setError(null);
    setDetail(null);
    fetchGiftCardDetail(id, signal)
      .then((res) => {
        if (signal.aborted) return;
        setDetail(res);
      })
      .catch((err: unknown) => {
        if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
        setError(err instanceof Error ? err.message : 'Failed to load this gift card');
      })
      .finally(() => {
        if (!signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [id]);

  return { detail, loading, error };
}
